"use client";

// React wrapper around the carousel. All the WebGL/animation logic lives in
// lib/carousel/engine.js — this component only owns the DOM overlay: the
// heading, the counter, the "View" cursor label and the Close button.
import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { PROJECTS, ENTRY, UI_ANIM } from "@/lib/carousel/config";
import { createCarousel } from "@/lib/carousel/engine";
import { createCarouselGui } from "@/lib/carousel/gui";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";
import { useEmberTheme } from "@/hooks/useEmberTheme";
const CarouselSection = t0 => {
  const $ = _c(79);
  const {
    footer
  } = t0;
  const mountRef = useRef(null);
  const cursorRef = useRef(null);
  const topTextRef = useRef(null);
  const counterRef = useRef(null);
  const footerOverlayRef = useRef(null);
  const engineRef = useRef(null);
  const revealPlayedRef = useRef(false);
  const isNeumorphic = useNeumorphicTheme();
  const isEmber = useEmberTheme();
  const [active, setActive] = useState(0);
  const [focused, setFocused] = useState(false);
  const [entryDone, setEntryDone] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleKeyDown = e => {
        if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) {
          return;
        }
        if (e.key === "Escape") {
          engineRef.current?.closeFocus();
        } else {
          if (e.key === "Enter") {
            engineRef.current?.openFocus();
          } else {
            if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") {
              engineRef.current?.nextPanel();
            } else {
              if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") {
                engineRef.current?.prevPanel();
              }
            }
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (!mountRef.current) {
        return;
      }
      const engine = createCarousel(mountRef.current, {
        cursorElement: cursorRef.current,
        onActiveChange: setActive,
        onFocusChange: setFocused,
        onEntryDone: setEntryDone
      });
      engineRef.current = engine;
      const gui = createCarouselGui(engine);
      return () => {
        gui.destroy();
        engine.destroy();
        engineRef.current = null;
      };
    };
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== isEmber || $[5] !== isNeumorphic) {
    t5 = () => {
      if (engineRef.current) {
        engineRef.current.setTheme({
          isNeumorphic,
          isEmber
        });
      }
    };
    t6 = [isNeumorphic, isEmber];
    $[4] = isEmber;
    $[5] = isNeumorphic;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!mountRef.current) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          engineRef.current?.startEntry();
          observer.disconnect();
        }
      }, {
        threshold: 0.2
      });
      observer.observe(mountRef.current);
      return () => observer.disconnect();
    };
    t8 = [];
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[10] !== entryDone || $[11] !== focused) {
    t9 = () => {
      if (!topTextRef.current || !counterRef.current) {
        return;
      }
      if (!entryDone && ENTRY.enabled) {
        gsap.set([topTextRef.current, counterRef.current], {
          autoAlpha: 0
        });
        revealPlayedRef.current = false;
        return;
      }
      gsap.set(topTextRef.current, {
        xPercent: -50
      });
      gsap.set(counterRef.current, {
        xPercent: -50
      });
      const y = focused ? UI_ANIM.topShiftVh / 100 * window.innerHeight : 0;
      if (entryDone && !focused && !revealPlayedRef.current) {
        revealPlayedRef.current = true;
        gsap.fromTo(topTextRef.current, {
          autoAlpha: 0
        }, {
          autoAlpha: 1,
          duration: UI_ANIM.revealDuration,
          ease: UI_ANIM.revealEase
        });
        gsap.fromTo(counterRef.current, {
          autoAlpha: 0
        }, {
          autoAlpha: 1,
          duration: UI_ANIM.revealDuration,
          ease: UI_ANIM.revealEase,
          delay: UI_ANIM.revealStagger
        });
        return;
      }
      gsap.to(topTextRef.current, {
        y,
        autoAlpha: 1,
        duration: UI_ANIM.duration,
        ease: UI_ANIM.ease
      });
      gsap.to([counterRef.current, footerOverlayRef.current], {
        autoAlpha: focused ? 0 : 1,
        duration: UI_ANIM.duration,
        ease: UI_ANIM.ease
      });
    };
    t10 = [focused, entryDone];
    $[10] = entryDone;
    $[11] = focused;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  const controlBadgeStyle = isNeumorphic ? "border border-[#a3b1c6] bg-[#e0e5ec] text-[#374151] shadow-[2px_2px_4px_rgba(163,177,198,0.6),_-2px_-2px_4px_rgba(255,255,255,0.5)]" : isEmber ? "border border-[#FF4F00] bg-[#1A1716] text-[#FF4F00] shadow-[2px_2px_0px_#FF4F00]" : "border border-white/40 bg-zinc-900/90 text-white";
  const t11 = `h-screen relative w-screen touch-pan-y select-none overflow-hidden transition-colors duration-300 ${isNeumorphic ? "bg-[#e0e5ec]" : isEmber ? "bg-[#1A1716]" : "bg-black"}`;
  const t12 = `absolute px-4 left-1/2 -translate-x-1/2 w-[92vw] max-w-2xl top-[12%] sm:top-[15%] text-center z-20 pointer-events-none ${isNeumorphic ? "text-[#1f2937]" : isEmber ? "text-[#FF4F00]" : "text-white"}`;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = ENTRY.enabled ? {
      opacity: 0,
      visibility: "hidden"
    } : undefined;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = `text-center text-base sm:text-lg font-bold tracking-wide break-words max-w-full ${isNeumorphic ? "text-[#111827]" : isEmber ? "text-[#FF4F00]" : "text-white"}`;
  const t15 = PROJECTS[active];
  let t16;
  if ($[15] !== t14 || $[16] !== t15.brand) {
    t16 = <p className={t14}>{t15.brand}</p>;
    $[15] = t14;
    $[16] = t15.brand;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  const t17 = `text-center text-xs sm:text-sm mt-[3px] break-words max-w-lg mx-auto leading-normal ${isNeumorphic ? "text-[#4b5563]" : isEmber ? "text-[#FF4F00]/90" : "text-white/80"}`;
  const t18 = PROJECTS[active];
  let t19;
  if ($[18] !== t17 || $[19] !== t18.desc) {
    t19 = <p className={t17}>{t18.desc}</p>;
    $[18] = t17;
    $[19] = t18.desc;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] !== t16 || $[22] !== t19) {
    t20 = <div className="flex flex-col items-center justify-center text-center">{t16}{t19}</div>;
    $[21] = t16;
    $[22] = t19;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== t12 || $[25] !== t20) {
    t21 = <div ref={topTextRef} className={t12} style={t13}>{t20}</div>;
    $[24] = t12;
    $[25] = t20;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  const t22 = `absolute px-4 left-1/2 bottom-[16%] text-center whitespace-nowrap z-20 ${isNeumorphic ? "text-[#1f2937]" : isEmber ? "text-[#FF4F00]" : "text-white"}`;
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = ENTRY.enabled ? {
      opacity: 0,
      visibility: "hidden"
    } : undefined;
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  const t24 = `text-center text-base font-bold tracking-widest drop-shadow-md ${isNeumorphic ? "text-[#111827]" : isEmber ? "text-[#FF4F00]" : "text-white"}`;
  const t25 = String(active + 1);
  let t26;
  if ($[28] !== t25) {
    t26 = t25.padStart(2, "0");
    $[28] = t25;
    $[29] = t26;
  } else {
    t26 = $[29];
  }
  let t27;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = String(PROJECTS.length).padStart(2, "0");
    $[30] = t27;
  } else {
    t27 = $[30];
  }
  let t28;
  if ($[31] !== t24 || $[32] !== t26) {
    t28 = <p className={t24}>{t26}/{t27}</p>;
    $[31] = t24;
    $[32] = t26;
    $[33] = t28;
  } else {
    t28 = $[33];
  }
  const t29 = `mt-2 hidden sm:flex items-center justify-center gap-1.5 text-xs font-mono font-bold uppercase tracking-wider ${isNeumorphic ? "text-[#4b5563]" : isEmber ? "text-[#FF4F00]" : "text-white/90"}`;
  const t30 = `px-1.5 py-0.5 shadow-xs ${controlBadgeStyle}`;
  let t31;
  if ($[34] !== t30) {
    t31 = <span className={t30}>← / →</span>;
    $[34] = t30;
    $[35] = t31;
  } else {
    t31 = $[35];
  }
  const t32 = `px-1.5 py-0.5 shadow-xs ${controlBadgeStyle}`;
  let t33;
  if ($[36] !== t32) {
    t33 = <span className={t32}>A / D</span>;
    $[36] = t32;
    $[37] = t33;
  } else {
    t33 = $[37];
  }
  let t34;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <span className="opacity-40">•</span>;
    $[38] = t34;
  } else {
    t34 = $[38];
  }
  const t35 = `px-1.5 py-0.5 shadow-xs ${controlBadgeStyle}`;
  let t36;
  if ($[39] !== t35) {
    t36 = <span className={t35}>ENTER</span>;
    $[39] = t35;
    $[40] = t36;
  } else {
    t36 = $[40];
  }
  let t37;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <span className="opacity-40">•</span>;
    $[41] = t37;
  } else {
    t37 = $[41];
  }
  const t38 = `px-1.5 py-0.5 shadow-xs ${controlBadgeStyle}`;
  let t39;
  if ($[42] !== t38) {
    t39 = <span className={t38}>ESC</span>;
    $[42] = t38;
    $[43] = t39;
  } else {
    t39 = $[43];
  }
  let t40;
  if ($[44] !== t29 || $[45] !== t31 || $[46] !== t33 || $[47] !== t36 || $[48] !== t39) {
    t40 = <div className={t29}>{t31}{t33}{t34}{t36}{t37}{t39}</div>;
    $[44] = t29;
    $[45] = t31;
    $[46] = t33;
    $[47] = t36;
    $[48] = t39;
    $[49] = t40;
  } else {
    t40 = $[49];
  }
  const t41 = `mt-2 sm:hidden flex items-center justify-center gap-2 text-[10px] font-mono font-bold uppercase tracking-wider ${isNeumorphic ? "text-[#4b5563]" : isEmber ? "text-[#FF4F00]" : "text-white/90"}`;
  const t42 = `px-2 py-0.5 shadow-xs ${controlBadgeStyle}`;
  let t43;
  if ($[50] !== t42) {
    t43 = <span className={t42}>SWIPE ← / →</span>;
    $[50] = t42;
    $[51] = t43;
  } else {
    t43 = $[51];
  }
  let t44;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span className="opacity-40">•</span>;
    $[52] = t44;
  } else {
    t44 = $[52];
  }
  const t45 = `px-2 py-0.5 shadow-xs ${controlBadgeStyle}`;
  let t46;
  if ($[53] !== t45) {
    t46 = <span className={t45}>TAP TO VIEW</span>;
    $[53] = t45;
    $[54] = t46;
  } else {
    t46 = $[54];
  }
  let t47;
  if ($[55] !== t41 || $[56] !== t43 || $[57] !== t46) {
    t47 = <div className={t41}>{t43}{t44}{t46}</div>;
    $[55] = t41;
    $[56] = t43;
    $[57] = t46;
    $[58] = t47;
  } else {
    t47 = $[58];
  }
  let t48;
  if ($[59] !== t22 || $[60] !== t28 || $[61] !== t40 || $[62] !== t47) {
    t48 = <div ref={counterRef} className={t22} style={t23}>{t28}{t40}{t47}</div>;
    $[59] = t22;
    $[60] = t28;
    $[61] = t40;
    $[62] = t47;
    $[63] = t48;
  } else {
    t48 = $[63];
  }
  let t49;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div ref={cursorRef} className="fixed top-4 left-4 z-50 pointer-events-none text-white text-sm whitespace-nowrap mix-blend-exclusion" style={{
      willChange: "transform"
    }}>View</div>;
    $[64] = t49;
  } else {
    t49 = $[64];
  }
  let t50;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = () => engineRef.current?.closeFocus();
    $[65] = t50;
  } else {
    t50 = $[65];
  }
  const t51 = `absolute bottom-20 left-1/2 -translate-x-1/2 sm:bottom-auto sm:left-auto sm:translate-x-0 sm:top-8 sm:right-8 z-40 px-5 py-2.5 sm:px-4 sm:py-2 font-black text-xs sm:text-sm uppercase tracking-wider transition-all duration-200 flex items-center gap-2 cursor-pointer ${isNeumorphic ? "bg-[#e0e5ec] text-[#111827] border-2 border-[#a3b1c6] shadow-[6px_6px_12px_rgba(163,177,198,0.6),_-6px_-6px_12px_rgba(255,255,255,0.5)] hover:bg-[#d1d9e6] active:shadow-[inset_4px_4px_8px_rgba(163,177,198,0.6),_inset_-4px_-4px_8px_rgba(255,255,255,0.5)]" : isEmber ? "bg-[#1A1716] text-[#FF4F00] border-3 sm:border-4 border-[#FF4F00] shadow-[4px_4px_0px_0px_#FF4F00] hover:bg-[#FF4F00] hover:text-[#1A1716] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_#FF4F00] active:translate-x-1 active:translate-y-1 active:shadow-none" : "bg-white text-black border-3 sm:border-4 border-black shadow-[4px_4px_0px_0px_#ffffff] hover:bg-black hover:text-white hover:border-white hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0px_0px_#ffffff] active:translate-x-1 active:translate-y-1 active:shadow-none"} ${focused ? "opacity-100 pointer-events-auto scale-100" : "opacity-0 pointer-events-none scale-95"}`;
  let t52;
  let t53;
  let t54;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <span className="text-base font-black">✕</span>;
    t53 = <span>CLOSE</span>;
    t54 = <span className="text-[10px] font-mono opacity-60 hidden sm:inline-block ms-1 border border-current px-1 py-0.5">[ESC]</span>;
    $[66] = t52;
    $[67] = t53;
    $[68] = t54;
  } else {
    t52 = $[66];
    t53 = $[67];
    t54 = $[68];
  }
  let t55;
  if ($[69] !== t51) {
    t55 = <button type="button" onClick={t50} aria-label="Close image view (press Esc)" className={t51}>{t52}{t53}{t54}</button>;
    $[69] = t51;
    $[70] = t55;
  } else {
    t55 = $[70];
  }
  let t56;
  if ($[71] !== footer) {
    t56 = footer && <div ref={footerOverlayRef} className="absolute bottom-0 left-0 right-0 z-30 pointer-events-auto">{footer}</div>;
    $[71] = footer;
    $[72] = t56;
  } else {
    t56 = $[72];
  }
  let t57;
  if ($[73] !== t11 || $[74] !== t21 || $[75] !== t48 || $[76] !== t55 || $[77] !== t56) {
    t57 = <div ref={mountRef} className={t11}>{t21}{t48}{t49}{t55}{t56}</div>;
    $[73] = t11;
    $[74] = t21;
    $[75] = t48;
    $[76] = t55;
    $[77] = t56;
    $[78] = t57;
  } else {
    t57 = $[78];
  }
  return t57;
};
export default CarouselSection;